import type { DocumentWithTimestamps, Timestamp } from './firebase.ts'
import type { UnregisteredUser } from './JobRequest.ts'
import type { OrgId, UserId } from './ids.ts'
import { StatusChangedBy } from './Mentoring.ts'
import type { OrgMeta } from './Org.ts'
import type { ProjectId } from './Project.ts'

export type ParticipationRequestId = string

export interface ParticipationRequestFormData {
    motivation: string
    projectId?: ProjectId
}

export interface VolunteeringWeekParticipationRequestFormData extends ParticipationRequestFormData {
    email: string
    phone: string
    volunteerCount: number
    backupContactMail?: string
    consent?: boolean
    unregisteredUser?: UnregisteredUser
    participantId?: UserId
    companyName?: string
}

export interface ParticipationRequestRejectionFormData {
    message?: string
}

export enum RequestorType {
    Volunteer = 'volunteer',
    Org = 'org'
}

export enum ProgressCheckInStatus {
    Pending = 'pending',
    Achieved = 'achieved',
    Failed = 'failed'
}

export enum RequestStatus {
    Requested = 'requested',
    InDiscussion = 'in_discussion',
    Rejected = 'rejected',
    Accepted = 'accepted',
    Finished = 'finished',
    Expired = 'expired',
    Cancelled = 'cancelled'
}

export type ParticipationRequest = ParticipationRequestFormData &
    ParticipationRequestRejectionFormData & // AD-HOC fix - needs to ged rid of this...
    DocumentWithTimestamps & {
        id: ParticipationRequestId
        participantId: UserId
        projectId: ProjectId
        orgId: OrgId
        status: RequestStatus
        requestorType: RequestorType
        progressCheckInStatus?: ProgressCheckInStatus
        orgMeta: OrgMeta
        participantMeta: {
            firstName: string
            lastName: string
            contactPhone?: string
            contactMail?: string
            backupContactMail?: string
        }
        statusChangedBy?: StatusChangedBy
        rejectedAt?: Timestamp
        expiredAt?: Timestamp
        acceptedAt?: Timestamp
        cancelledAt?: Timestamp
        responsesAverageTime?: number
        responsesAmount?: number
        createdById?: UserId
        acceptedById?: UserId
        postponedUntil?: Timestamp
        postponedOrgFeedbackUntil?: Timestamp
        postponedParticipantFeedbackUntil?: Timestamp
        deadlineAt?: Timestamp
        volunteerCount?: number
        companyName?: string
        // Organisation's own pipeline position while the request is open. Terminal columns are
        // derived from `status`, so this is only meaningful for requested / in_discussion.
        stage?: ParticipationRequestStage
        stageUpdatedAt?: Timestamp
        stageUpdatedById?: UserId
    }

/**
 * Organisation-side pipeline for volunteers, shown as a kanban on the project activity page.
 *
 * Only the open stages (new / contacted / meeting) are stored — the organisation sets them by hand.
 * Active, finished and declined always follow `status`, because many flows change it without the
 * organisation touching the board: the volunteer answers an invitation or ends the cooperation, and
 * Bloomreach expires unanswered requests.
 */
export enum ParticipationRequestStage {
    DECLINED = 'declined',
    NEW = 'new',
    CONTACTED = 'contacted',
    MEETING = 'meeting',
    ACTIVE = 'active',
    FINISHED = 'finished'
}

export const participationRequestStageDict: { [key in ParticipationRequestStage]: string } = {
    [ParticipationRequestStage.DECLINED]: 'Zamietnutý',
    [ParticipationRequestStage.NEW]: 'Nový',
    [ParticipationRequestStage.CONTACTED]: 'V kontakte',
    [ParticipationRequestStage.MEETING]: 'Stretnutie',
    [ParticipationRequestStage.ACTIVE]: 'Aktívny',
    [ParticipationRequestStage.FINISHED]: 'Ukončený'
}

/** Kanban column order — declined sits apart on the left, like on the jobs board. */
export const participationRequestStageOrder: ParticipationRequestStage[] = [
    ParticipationRequestStage.DECLINED,
    ParticipationRequestStage.NEW,
    ParticipationRequestStage.CONTACTED,
    ParticipationRequestStage.MEETING,
    ParticipationRequestStage.ACTIVE,
    ParticipationRequestStage.FINISHED
]

/** Stages the organisation sets directly; the rest go through approve / reject / cancel-by-org. */
export const participationRequestOpenStages: ParticipationRequestStage[] = [
    ParticipationRequestStage.NEW,
    ParticipationRequestStage.CONTACTED,
    ParticipationRequestStage.MEETING
]

const openRequestStatuses: RequestStatus[] = [RequestStatus.Requested, RequestStatus.InDiscussion]

export function getParticipationRequestStage(
    request: Pick<ParticipationRequest, 'stage' | 'status'>
): ParticipationRequestStage {
    switch (request.status) {
        case RequestStatus.Accepted:
            return ParticipationRequestStage.ACTIVE
        case RequestStatus.Finished:
            return ParticipationRequestStage.FINISHED
        case RequestStatus.Rejected:
        case RequestStatus.Expired:
        case RequestStatus.Cancelled:
            return ParticipationRequestStage.DECLINED
    }

    if (request.stage && participationRequestOpenStages.includes(request.stage)) {
        return request.stage
    }

    // A chat reply moves the request to in_discussion before anyone touched the board.
    return request.status === RequestStatus.InDiscussion
        ? ParticipationRequestStage.CONTACTED
        : ParticipationRequestStage.NEW
}

/**
 * Status to store alongside an open stage. Anything past "new" means the organisation has
 * responded, which is what in_discussion already means to the chat and the expiry scenario.
 * Moving back to "new" never reverts in_discussion — that would re-arm the first-response emails.
 */
export function getParticipationRequestStatusForStage(
    stage: ParticipationRequestStage,
    currentStatus: RequestStatus
): RequestStatus {
    return stage === ParticipationRequestStage.NEW && currentStatus === RequestStatus.Requested
        ? RequestStatus.Requested
        : RequestStatus.InDiscussion
}

/**
 * Fields to write when the organisation moves a request to an open stage. Shared by the API and the
 * MCP tool so both leave the request in the same state. `answersVolunteer` means the volunteer was
 * still waiting for a first reply, so the organisation's "volunteer is waiting" popup should be archived.
 */
export function getParticipationRequestStageUpdate(
    request: Pick<ParticipationRequest, 'stage' | 'status' | 'requestorType'>,
    stage: ParticipationRequestStage,
    userId: UserId,
    now: Date = new Date()
) {
    const status = getParticipationRequestStatusForStage(stage, request.status)
    const isReopened = getParticipationRequestStage(request) === ParticipationRequestStage.DECLINED

    return {
        update: {
            stage,
            status,
            stageUpdatedAt: now,
            stageUpdatedById: userId,
            updatedAt: now,
            ...(isReopened && { rejectedAt: null, expiredAt: null })
        },
        answersVolunteer:
            request.requestorType === RequestorType.Volunteer &&
            request.status === RequestStatus.Requested &&
            status !== RequestStatus.Requested
    }
}

/**
 * Whether the organisation may move a request to `target`. Shared by the board (to block drops),
 * the API and the MCP tool (to reject them), so all enforce the same rules.
 */
export function canMoveParticipationRequestToStage(
    request: Pick<ParticipationRequest, 'stage' | 'status' | 'requestorType' | 'statusChangedBy' | 'participantId'>,
    target: ParticipationRequestStage
): boolean {
    const current = getParticipationRequestStage(request)

    if (current === target) {
        return false
    }

    const isInvitation = request.requestorType === RequestorType.Org

    switch (current) {
        case ParticipationRequestStage.ACTIVE:
            // Leaving an active cooperation always means ending it.
            return target === ParticipationRequestStage.FINISHED && Boolean(request.participantId)

        case ParticipationRequestStage.FINISHED:
            return false

        case ParticipationRequestStage.DECLINED: {
            // The volunteer said no (or withdrew) — that is theirs to undo, not the organisation's.
            // Only the volunteer can reject an invitation, so a rejected one counts even without
            // `statusChangedBy`, which older documents lack.
            const declinedByVolunteer =
                request.status === RequestStatus.Cancelled ||
                (request.status === RequestStatus.Rejected &&
                    (isInvitation || request.statusChangedBy === StatusChangedBy.Volunteer))

            if (declinedByVolunteer) {
                return false
            }

            if (participationRequestOpenStages.includes(target)) {
                return true
            }

            return target === ParticipationRequestStage.ACTIVE && !isInvitation && Boolean(request.participantId)
        }

        default:
            if (!openRequestStatuses.includes(request.status)) {
                return false
            }

            if (participationRequestOpenStages.includes(target)) {
                return true
            }

            // An invitation is accepted or declined by the volunteer, never by the organisation.
            if (target === ParticipationRequestStage.ACTIVE) {
                return !isInvitation && Boolean(request.participantId)
            }

            return target === ParticipationRequestStage.DECLINED && !isInvitation
    }
}

// Internal note on a volunteer (e.g. recap of a call). Stored in the org-only
// participation-requests/{id}/notes subcollection, never on the request the volunteer can read.
export interface ParticipationRequestNote {
    id: string
    text: string
    authorId?: UserId
    authorName?: string
    createdAt: Timestamp
}
